/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes */
// Extends from
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import { tagName } from '@ember-decorators/component';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';

/**
 * PDF Viewer Component
 *
 * Embeds a PDF using PDF.js viewer inside an iframe.
 * The viewer URL is generated from the `src` parameter and points to a local PDF.js instance.
 *
 * If you need to upgrade PDF.js see: https://linced.atlassian.net/wiki/spaces/DEVKB/pages/3236659221/PDF.js
 *
 * @class PdfViewerComponent
 * @extends Ember.Component
 *
 * @param {Ember.Model<module:@linc-technologies/linced-common/models/media>|null} media - The media record of the PDF file to be rendered.
 * @param {string|null} height - Optional height of the iframe (e.g., `"600px"`, `"100%"`). Defaults to `"100%"`.
 * @param {string|null} width - Optional width of the iframe (e.g., `"800px"`, `"100%"`). Defaults to `"100%"`.
 */
@tagName('')
export default class PdfViewer extends Component {
  /** @type {string} The version of pdf.js to use. */
  version = 'v6.4.299';

  @service('media') mediaService;

  // params
  /** @type {Ember.Model<module:@linc-technologies/linced-common/models/media>|null} - The media record of the PDF file to be rendered. */
  media = null;

  /** @type {string|null} Optional iframe height. Defaults to "100%". */
  height = null;

  /** @type {string|null} Optional iframe width. Defaults to "100%". */
  width = null;

  /** @type {string|null} The resolved blob url (or rawUrl fallback) for the current media. Populated by resolveUrlTask. */
  @tracked resolvedUrl = null;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.resolveUrlTask.perform();
  }

  // Resolves the PDF's blob url for internal media, mirroring what the
  // media model's now-removed didCreate/didLoad hooks used to do via
  // onMediaCreateOrLoad. Non-internal PDFs fall back to rawUrl, same as the
  // old url computed did.
  resolveUrlTask = restartableTask(async () => {
    let media = this.media;
    if (!media) {
      this.resolvedUrl = null;
      return;
    }

    let isInternalPdf =
      media.mimeType === 'application/pdf' &&
      media.mediaSource === MediaSource.INTERNAL;
    if (!isInternalPdf) {
      this.resolvedUrl = media.rawUrl;
      return;
    }

    let url = await this.mediaService.getFileBlobUri(media.id);
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.resolvedUrl = url || media.rawUrl;
  });

  /**
   * Computed property that returns the PDF title.
   *
   * @memberof PdfViewerComponent
   * @instance
   * @returns {string|null} Optional iframe title. Defaults to "PDF Viewer". */
  @computed('media.decodedFileName')
  get title() {
    if (!this.media) {
      return 'PDF Document';
    }

    return this.media.decodedFileName;
  }

  /**
   * Computed property that generates the PDF.js viewer URL.
   * If `src` is not set, returns `null`.
   *
   * @memberof PdfViewerComponent
   * @instance
   * @returns {string|null} Full URL to the PDF.js viewer with the file query parameter encoded.
   */
  get viewerUrl() {
    if (!this.resolvedUrl) {
      return null;
    }

    // Supported PDF Viewer Params can be found via the chromium repo: https://github.com/chromium/chromium/blob/a39205c68f094eda261a218cd819a792840dd783/chrome/browser/resources/pdf/open_pdf_params_parser.ts#L11-L18
    let url = encodeURIComponent(this.resolvedUrl + '#navpanes=0');
    return `/assets/pdfjs/${this.version}/web/viewer.html?file=${url}`;
  }
}
